import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useCallback, useRef, useState } from 'react'
import type React from 'react'

interface TruncatedCellProps {
	children: React.ReactNode
	maxWidth?: number | string // Allow custom max width
	className?: string
	align?: 'left' | 'center' | 'right'
	showTooltip?: boolean
	tooltipSide?: 'top' | 'bottom' | 'left' | 'right'
	maxTooltipWidth?: string
	tooltipContent?: string
	/**
	 * Override the default collision boundary. Useful when the cell is inside an `overflow-hidden` container
	 * (e.g., a bottom bar or drawer) that forces the tooltip to incorrectly flip its position.
	 */
	collisionBoundary?: Element | null | undefined
}

export function TruncatedCell({
	children,
	maxWidth,
	className = '',
	align = 'left',
	showTooltip = true,
	tooltipSide = 'top',
	maxTooltipWidth = 'max-w-md',
	tooltipContent,
	collisionBoundary,
}: TruncatedCellProps) {
	const textRef = useRef<HTMLDivElement | null>(null)
	const [isOverflowing, setIsOverflowing] = useState(false)

	// A ref callback rather than an effect: wrapping the cell in a tooltip replaces this div, and the observers must follow it.
	const observeOverflow = useCallback((element: HTMLDivElement | null) => {
		textRef.current = element
		if (!element) {
			return
		}
		let timeout: ReturnType<typeof setTimeout> | undefined
		const measure = () => {
			clearTimeout(timeout)
			timeout = setTimeout(() => setIsOverflowing(element.scrollWidth > element.clientWidth), 0)
		}
		measure()
		const resizeObserver = new ResizeObserver(measure)
		resizeObserver.observe(element)
		const mutationObserver = new MutationObserver(measure)
		mutationObserver.observe(element, { childList: true, subtree: true, characterData: true })
		return () => {
			clearTimeout(timeout)
			resizeObserver.disconnect()
			mutationObserver.disconnect()
			if (textRef.current === element) {
				textRef.current = null
			}
		}
	}, [])

	const alignmentClass = {
		left: 'text-left',
		center: 'text-center',
		right: 'text-right',
	}[align]

	// Always apply truncation styles to prevent mount jitter. The Tooltip handles showing full text.
	const truncateStyles = 'overflow-hidden text-ellipsis whitespace-nowrap'

	const content = (
		<div
			ref={observeOverflow}
			className={`block w-full min-w-0 ${truncateStyles} ${alignmentClass} ${className}`}
			style={
				maxWidth !== undefined ? { maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth } : undefined
			}>
			{children}
		</div>
	)

	// Only show tooltip if text is overflowing and showTooltip is true
	if (!isOverflowing || !showTooltip) {
		return content
	}

	return (
		<Tooltip>
			{/* The full text is already in the DOM; a tab stop per cell would bury the page's real controls. */}
			<TooltipTrigger asChild focusable={false}>
				{content}
			</TooltipTrigger>
			<TooltipContent
				side={tooltipSide}
				align='start'
				className={`${maxTooltipWidth} wrap-break-word`}
				sideOffset={8}
				avoidCollisions={true}
				// Use the custom boundary if provided, otherwise fallback to local containers or document body.
				// Explicitly checking `!== undefined` to allow passing `null` to bypass local boundaries.
				collisionBoundary={
					collisionBoundary !== undefined
						? collisionBoundary || undefined
						: textRef.current?.closest('[class*="bg-card"]') ||
							textRef.current?.closest('[class*="overflow-hidden"]') ||
							textRef.current?.closest('.CommonTable') ||
							document.body
				}
				collisionPadding={10}>
				{tooltipContent ? <p>{tooltipContent}</p> : <div>{children}</div>}
			</TooltipContent>
		</Tooltip>
	)
}
